import minify from 'pg-minify';
import { VisualizationType } from '@/api';
import Dataset from './dataset';
import Dashboard from '@/services/dashboard';
import { DATASET_PREVIEW_LIMIT } from '@/utils/constants';
import { graphql } from '@/utils/graphql';
import { logger } from '@/utils/logger';
import { getPropertyValue, checkNonEmptyArray, checkNonEmptyString } from '@/utils/typeChecks';
import {
  DATA_TYPES,
  SERIE_TYPES,
  SeriesField,
  TitleAlignment,
  DeltaIndicator,
  ColumnFormatConfig,
} from '@/components/Form/VisualizationTypeForm/initialValues';
import type { Sorting } from '@/models/objects/datasetViewState';
import {
  buildOrderByClause,
  buildSelectClause,
  getSortOnlyColumns,
} from '@/components/Form/VisualizationTypeForm/SortConfigurationPanel/sortListUtils';

const ns = 'app.services.visualization';

type ChartVisualizationConfiguration = {
  titleAlignment?: TitleAlignment;
  xAxisColumn?: string;
  yAxisColumn?: string;
};

type PieChartVisualizationConfiguration = ChartVisualizationConfiguration & {
  metric?: string;
};

export type BarChartVisualizationConfiguration = ChartVisualizationConfiguration & {
  isStacked: boolean;
  isHorizontal: boolean;
  xLabel: string;
  xAxisDataType?: typeof DATA_TYPES[number];
  yLabel: string;
  yAxisDataType?: typeof DATA_TYPES[number];
  yAxisColumns: string[];
  series: SeriesField;
  groupBy?: string; // either groupBy or series is required where groupBy is used when only one yAxisColumns
  orderByColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
};

export type ColumnMetricVisualizationConfiguration = {
  titleAlignment?: TitleAlignment;
  hideTitle?: boolean;
  column?: string;
  metric?: string;
  description: string;
  prefix?: string;
  suffix?: string;
  valueFormat?: string;
  decimals?: number;
  currency?: string;
  goalValue?: number;
  goalValueColumn?: string;
  deltaIndicator?: DeltaIndicator;
  showComparisonValue?: boolean;
};

type SingleGaugeVisualizationConfiguration = {
  titleAlignment?: TitleAlignment;
  column?: string;
  metric?: string;
  maxValue?: number;
  maxValueColumn?: string;
  maxValueAgg?: string;
  descValueColumn?: string;
  descValueAgg?: string;
  description?: string;
  descriptionBelowChart?: boolean;
  customQuery?: string;
  prefix?: string;
  suffix?: string;
  valueFormat?: string;
  decimals?: number;
  currency?: string;
};

export type TableVisualizationConfiguration = {
  titleAlignment?: TitleAlignment;
  columns: string[];
  headerBackgroundColor?: string;
  headerFontColor?: string;
  showRowNumbers?: boolean;
  columnFormats?: ColumnFormatConfig;
  columnTotals?: string[];
  sortings?: Sorting[];
  hidePaginationWhenSinglePage?: boolean;
  hideHiddenSortColumns?: boolean;
};

type VisualizationConfiguration<T extends VisualizationType> = T extends VisualizationType.BAR_CHART
  ? BarChartVisualizationConfiguration
  : T extends VisualizationType.LINE_CHART
  ? BarChartVisualizationConfiguration
  : T extends VisualizationType.PIE_CHART
  ? PieChartVisualizationConfiguration
  : T extends VisualizationType.DOUGHNUT_CHART
  ? PieChartVisualizationConfiguration
  : T extends VisualizationType.GAUGE_CHART
  ? SingleGaugeVisualizationConfiguration
  : T extends VisualizationType.COLUMN_METRIC
  ? ColumnMetricVisualizationConfiguration
  : T extends VisualizationType.TABLE
  ? TableVisualizationConfiguration
  : never;

function checkOneSerie(val: unknown): boolean {
  return (
    typeof val === 'object' &&
    val !== null &&
    checkNonEmptyString(typeof getPropertyValue(val, 'name')) &&
    SERIE_TYPES.some((el) => el === getPropertyValue(val, 'type'))
  );
}

export function checkIsVisualizationConfiguration<T extends VisualizationType>(
  type: T,
  val: unknown,
): boolean {
  return (
    typeof val === 'object' &&
    val !== null &&
    (((type === VisualizationType.BAR_CHART || type === VisualizationType.LINE_CHART) &&
      typeof getPropertyValue(val, 'isStacked') === 'boolean' &&
      typeof getPropertyValue(val, 'isHorizontal') === 'boolean' &&
      typeof getPropertyValue(val, 'xLabel') === 'string' &&
      typeof getPropertyValue(val, 'yLabel') === 'string' &&
      (typeof getPropertyValue(val, 'xAxisDataType') === 'string' ||
        typeof getPropertyValue(val, 'xAxisDataType') === 'undefined') &&
      (typeof getPropertyValue(val, 'yAxisDataType') === 'string' ||
        typeof getPropertyValue(val, 'yAxisDataType') === 'undefined') &&
      (typeof getPropertyValue(val, 'orderByColumn') === 'string' ||
        typeof getPropertyValue(val, 'orderByColumn') === 'undefined') &&
      (typeof getPropertyValue(val, 'sortDirection') === 'string' ||
        typeof getPropertyValue(val, 'sortDirection') === 'undefined') &&
      checkNonEmptyString(getPropertyValue(val, 'xAxisColumn')) &&
      checkNonEmptyArray(getPropertyValue(val, 'yAxisColumns'), checkNonEmptyString) &&
      (checkNonEmptyArray(getPropertyValue(val, 'series'), checkOneSerie) ||
        checkNonEmptyString(getPropertyValue(val, 'groupBy')))) ||
      // -- //
      ((type === VisualizationType.PIE_CHART || type === VisualizationType.DOUGHNUT_CHART) &&
        checkNonEmptyString(getPropertyValue(val, 'xAxisColumn')) &&
        checkNonEmptyString(getPropertyValue(val, 'yAxisColumn'))) ||
      // -- //
      (type === VisualizationType.GAUGE_CHART &&
        checkNonEmptyString(getPropertyValue(val, 'column')) &&
        checkNonEmptyString(getPropertyValue(val, 'metric')) &&
        typeof getPropertyValue(val, 'description') === 'string' &&
        (typeof getPropertyValue(val, 'maxValue') === 'number' ||
          checkNonEmptyString(getPropertyValue(val, 'maxValueColumn')))) ||
      // -- //
      (type === VisualizationType.COLUMN_METRIC &&
        checkNonEmptyString(getPropertyValue(val, 'column')) &&
        checkNonEmptyString(getPropertyValue(val, 'metric')) &&
        typeof getPropertyValue(val, 'description') === 'string') ||
      // -- //
      (type === VisualizationType.TABLE &&
        checkNonEmptyArray(getPropertyValue(val, 'columns'), checkNonEmptyString)))
  );
}

function assertIsVisualizationConfiguration<T extends VisualizationType>(
  type: T,
  val: unknown,
  label: string,
): asserts val is VisualizationConfiguration<T> {
  if (!checkIsVisualizationConfiguration(type, val)) throw new Error(label);
}

export type VisualizationTypeAndConfiguration =
  | {
      type: VisualizationType.BAR_CHART;
      configurations: BarChartVisualizationConfiguration;
    }
  | {
      type: VisualizationType.LINE_CHART;
      configurations: BarChartVisualizationConfiguration;
    }
  | {
      type: VisualizationType.PIE_CHART;
      configurations: PieChartVisualizationConfiguration;
    }
  | {
      type: VisualizationType.DOUGHNUT_CHART;
      configurations: PieChartVisualizationConfiguration;
    }
  | {
      type: VisualizationType.GAUGE_CHART;
      configurations: SingleGaugeVisualizationConfiguration;
    }
  | {
      type: VisualizationType.COLUMN_METRIC;
      configurations: ColumnMetricVisualizationConfiguration;
    }
  | {
      type: VisualizationType.TABLE;
      configurations: TableVisualizationConfiguration;
    };

function assertIsVisualizationTypeConfiguration(
  val: { type: VisualizationType; configurations: VisualizationConfiguration<VisualizationType> },
  label: string,
): asserts val is VisualizationTypeAndConfiguration {
  const { type, configurations } = val;
  if (type === VisualizationType.BAR_CHART) {
    assertIsVisualizationConfiguration(type, configurations, `${label}|${type}`);
  } else if (type === VisualizationType.LINE_CHART) {
    assertIsVisualizationConfiguration(type, configurations, `${label}|${type}`);
  } else if (type === VisualizationType.PIE_CHART) {
    assertIsVisualizationConfiguration(type, configurations, `${label}|${type}`);
  } else if (type === VisualizationType.DOUGHNUT_CHART) {
    assertIsVisualizationConfiguration(type, configurations, `${label}|${type}`);
  } else if (type === VisualizationType.GAUGE_CHART) {
    assertIsVisualizationConfiguration(type, configurations, `${label}|${type}`);
  } else if (type === VisualizationType.COLUMN_METRIC) {
    assertIsVisualizationConfiguration(type, configurations, `${label}|${type}`);
  } else if (type === VisualizationType.TABLE) {
    assertIsVisualizationConfiguration(type, configurations, `${label}|${type}`);
  } else {
    throw new Error('BEEM250725141517');
  }
}

type ConstructorInput = {
  id: string;
  name: string;
  datasetId: string;
  dataset?: { name: string };
  typeAndConfigurations: VisualizationTypeAndConfiguration;
};

type CreateInput = {
  dataset: Dataset;
  name: string;
  typeAndConfigurations: VisualizationTypeAndConfiguration;
};

type UpdateInput = {
  id: string;
  name: string;
  typeAndConfigurations: VisualizationTypeAndConfiguration;
};

export default class Visualization {
  readonly id: string;

  readonly name: string;

  readonly datasetId: string;

  #datasetName: string;

  get datasetName(): string {
    if (!this.#datasetName) throw new Error('BEEM250122142947');
    return this.#datasetName;
  }

  #typeAndConfigurations: VisualizationTypeAndConfiguration;

  get typeAndConfigurations(): VisualizationTypeAndConfiguration {
    return {
      type: this.#typeAndConfigurations.type,
      configurations: { ...this.#typeAndConfigurations.configurations },
    } as VisualizationTypeAndConfiguration;
  }

  get type(): VisualizationType {
    return this.#typeAndConfigurations.type;
  }

  get configurations(): VisualizationConfiguration<VisualizationType> {
    return this.#typeAndConfigurations.configurations;
  }

  private constructor(input: ConstructorInput) {
    this.id = input.id;
    this.name = input.name;
    this.datasetId = input.datasetId;
    this.#datasetName = input.dataset?.name ?? '';
    this.#typeAndConfigurations = input.typeAndConfigurations;

    this.getVisualizationQuery = this.getVisualizationQuery.bind(this);
  }

  getVisualizationQuery(
    datasetQuery: string,
    isLimited: boolean,
  ): {
    vizQuery: string;
    wrapperQuery?: string;
    aggregateWrapperQuery?: string;
    displayColumns?: string[];
  } {
    return Visualization.getVisualizationQuery(
      this.#typeAndConfigurations,
      datasetQuery,
      isLimited,
    );
  }

  static async getVisualizationById(id: string): Promise<Visualization> {
    logger.info({ label: `${ns}.getVisualizationById.id`, message: id });
    const el = await graphql('getDatasetVisualization', { id });
    if (!el) throw new Error(`Visualization not found. It may have been deleted or is missing.`);
    const typeAndConfigurations = {
      type: el.type,
      configurations: JSON.parse(el.configurations),
    };
    assertIsVisualizationTypeConfiguration(typeAndConfigurations, `BEEM250122142949|${el.id}`);
    return new Visualization({ ...el, typeAndConfigurations });
  }

  static async getVisualizationsByDataset(datasetId: string): Promise<Visualization[]> {
    logger.info({ label: `${ns}.getVisualizationsByDataset.datasetId`, message: datasetId });
    const data = await graphql('visualizationsByDataset', { datasetId });

    return data.items.map((el) => {
      const typeAndConfigurations = {
        type: el.type,
        configurations: JSON.parse(el.configurations),
      };
      assertIsVisualizationTypeConfiguration(typeAndConfigurations, `BEEM250122142948|${el.id}`);
      return new Visualization({ ...el, typeAndConfigurations });
    });
  }

  static async getVisualizationsByWorkspace(workspaceId: string): Promise<Visualization[]> {
    logger.info({ label: `${ns}.getVisualizationsByWorkspace.workspaceId`, message: workspaceId });
    const data = await graphql('listVisualizationsByWorkspace', { workspaceId });

    return data.items.map((el) => {
      const typeAndConfigurations = {
        type: el.type,
        configurations: JSON.parse(el.configurations),
      };
      assertIsVisualizationTypeConfiguration(typeAndConfigurations, `BEEM250122142950|${el.id}`);
      return new Visualization({ ...el, typeAndConfigurations });
    });
  }

  static getVisualizationQuery(
    typeAndConfigurations: VisualizationTypeAndConfiguration,
    datasetQuery: string,
    isLimited: boolean,
  ): {
    vizQuery: string;
    wrapperQuery?: string;
    aggregateWrapperQuery?: string;
    displayColumns?: string[];
  } {
    const { type, configurations } = typeAndConfigurations;

    let vizQuery = minify(datasetQuery).trim().replace(/;$/, '');
    if (isLimited) {
      vizQuery = `${vizQuery} LIMIT ${DATASET_PREVIEW_LIMIT}`;
    }

    if (type === VisualizationType.BAR_CHART || type === VisualizationType.LINE_CHART) {
      const { xAxisColumn, yAxisColumns, series, groupBy } = configurations;
      if (yAxisColumns.length === 1 && groupBy) {
        if (!xAxisColumn || !yAxisColumns[0]) throw new Error('BEEM250313201255');

        return {
          vizQuery,
          wrapperQuery: `
            SELECT "${xAxisColumn}",
                  "${groupBy}",
                  SUM("${yAxisColumns[0]}") AS "${yAxisColumns[0]}"
            FROM ({{{{BEEM_QUERY_PLACEHOLDER}}}}) nested
            GROUP BY "${xAxisColumn}", "${groupBy}"
            ORDER BY "${xAxisColumn}"
          `,
        };
      } else {
        if (series.some((el) => !!el.aggregate)) {
          return {
            vizQuery,
            wrapperQuery: `
              SELECT "${xAxisColumn}"
                ${series.map((s) => `, ${s.aggregate}("${s.name}") AS "${s.name}"`).join('')}
              FROM ({{{{BEEM_QUERY_PLACEHOLDER}}}}) nested
              GROUP BY "${xAxisColumn}"
            `,
          };
        } else {
          return { vizQuery };
        }
      }
    }

    if (type === VisualizationType.PIE_CHART || type === VisualizationType.DOUGHNUT_CHART) {
      const { xAxisColumn, yAxisColumn, metric } = configurations;
      if (metric) {
        return {
          vizQuery,
          wrapperQuery: `
            SELECT "${xAxisColumn}",
                   ${metric}("${yAxisColumn}") AS "${yAxisColumn}"
            FROM ({{{{BEEM_QUERY_PLACEHOLDER}}}}) nested
            GROUP BY "${xAxisColumn}"
          `,
        };
      }
      return { vizQuery };
    }

    if (type === VisualizationType.COLUMN_METRIC) {
      const { column, metric, goalValueColumn } = configurations;

      if (goalValueColumn) {
        return {
          vizQuery,
          wrapperQuery: `
            SELECT count(*) AS "row_count",
                   ${metric}("${column}") AS "metric_value",
                   ${metric}("${goalValueColumn}") AS "goal_value"
            FROM ({{{{BEEM_QUERY_PLACEHOLDER}}}}) nested
          `,
        };
      }

      return {
        vizQuery,
        wrapperQuery: `
          SELECT count(*) AS "row_count",
                 ${metric}("${column}") AS "metric_value"
          FROM ({{{{BEEM_QUERY_PLACEHOLDER}}}}) nested
        `,
      };
    }

    if (type === VisualizationType.GAUGE_CHART) {
      const { column, metric, maxValueColumn, maxValueAgg, descValueColumn, descValueAgg } =
        configurations;

      const descValueSelect = descValueColumn
        ? `,\n                   ${descValueAgg || metric}("${descValueColumn}") AS "desc_value"`
        : '';

      if (maxValueColumn) {
        // Use maxValueAgg if specified, otherwise fall back to the metric aggregate
        const aggFunction = maxValueAgg || metric;
        return {
          vizQuery,
          wrapperQuery: `
            SELECT count(*) AS "row_count",
                   ${metric}("${column}") AS "metric_value",
                   ${aggFunction}("${maxValueColumn}") AS "max_value"${descValueSelect}
            FROM ({{{{BEEM_QUERY_PLACEHOLDER}}}}) nested
          `,
        };
      }

      // Otherwise, use static maxValue (backward compatibility)
      return {
        vizQuery,
        wrapperQuery: `
          SELECT count(*) AS "row_count",
                 ${metric}("${column}") AS "metric_value"${descValueSelect}
          FROM ({{{{BEEM_QUERY_PLACEHOLDER}}}}) nested
        `,
      };
    }

    if (type === VisualizationType.TABLE) {
      const { columns, columnTotals, sortings } = configurations;
      const orderByClause = buildOrderByClause(sortings);
      const sortOnlyCols = getSortOnlyColumns(sortings || [], columns);
      const selectClause = buildSelectClause(columns, sortings);
      const result: {
        vizQuery: string;
        wrapperQuery: string;
        aggregateWrapperQuery?: string;
        displayColumns?: string[];
      } = {
        vizQuery,
        wrapperQuery: `
          SELECT ${selectClause}
          FROM ({{{{BEEM_QUERY_PLACEHOLDER}}}}) nested${orderByClause}
        `,
      };
      if (sortOnlyCols.length > 0) {
        result.displayColumns = columns;
      }
      if (columnTotals && columnTotals.length > 0) {
        result.aggregateWrapperQuery = `
          SELECT ${columnTotals.map((c) => `SUM("${c}") AS "${c}"`).join(', ')}
          FROM ({{{{BEEM_QUERY_PLACEHOLDER}}}}) nested
        `;
      }
      return result;
    }

    throw new Error('BEEM25011205300');
  }

  static async create(input: CreateInput): Promise<string> {
    logger.info({ label: `${ns}.create.input`, message: input });
    const { dataset, name, typeAndConfigurations } = input;
    const data = await graphql('createDatasetVisualization', {
      input: {
        datasetId: dataset.id,
        workspaceId: dataset.workspaceId,
        name,
        type: typeAndConfigurations.type,
        configurations: JSON.stringify(typeAndConfigurations.configurations),
      },
    });
    await Dataset.updateVizCount(dataset.id);
    return data.id;
  }

  static async update(input: UpdateInput): Promise<string> {
    logger.info({ label: `${ns}.update.input`, message: input });
    const { id, name, typeAndConfigurations } = input;
    const data = await graphql('updateDatasetVisualization', {
      input: {
        id,
        name,
        type: typeAndConfigurations.type,
        configurations: JSON.stringify(typeAndConfigurations.configurations),
      },
    });
    return data.id;
  }

  static async delete({ id }: { id: string }): Promise<void> {
    logger.info({ label: `${ns}.delete.id`, message: id });
    const { datasetId } = await Visualization.getVisualizationById(id);
    await graphql('deleteDatasetVisualization', { input: { id } });
    // Req 2.1/2.2 — notify beem-agent after deleteDatasetVisualization succeeds.
    // Req 2.3 — fire-and-forget: failure must not surface to invokeDelete.
    // Req 2.4 — called exactly once per invocation.
    try {
      await Dashboard.notifyVizDeleted({ datasetId, vizId: id });
    } catch (e) {
      logger.error({
        label: `${ns}.delete.notifyVizDeleted.error`,
        message: { vizId: id, datasetId },
        error: e,
      });
    }
    await Dataset.updateVizCount(datasetId);
  }
}
